import { isString } from "lodash"
import { AddInventoryStandard, CategoryAdd, CategoryUpdate, InventoryStandard, Product, ProductAdd, ProductCategory, ProductDeprecatedEnum, ProductPrice, ProductStoreRequire, ProductStoreRequireAdd, ProductUpdate } from "../types/product"
import request from "."
import { isArray, isCategory, isProduct, isStringEmpty } from "../utils/checkTool"
import { cleanToCategory, cleanToProduct } from "../utils/cleanData"
import { CommonListParams } from "../types/common"
import { getStorage } from "@/utils/storage"

export type CategoryUpdateKey = 'name' | 'desc' | 'hsCode';

const url = '/wms/product'
const urlCategory = '/wms/supplierPriceRequest'

const PRODUCT_APIS = {
    categoryAdd: url + `/category/add`,
    categoryFetch: (id: string) => url + `/category/${id}/fetch`,
    categories: url + `/categories`,
    categoriesFetch: url + `/categories/fetch`,
    categoryProductAdd: (id: string) => url + `/category/${id}/product/add`,
    productAdd: url + `/add`,
    productSAdd: url + `/s/add`,
    productFetch: (id: string) => url + `/${id}/fetch`,
    productListByRootCategory: (id: string) => url + `/rootCategory/${id}/product/list`,
    productList: (id?: string) => url + `${id ? '/category/' + id + '/product' : ''}/list`,
    productFetchs: url + `/fetchs`,
    categoryProductFetch: (id: string) => url + `/category/${id}/product/list/fetch`,
    categoryUpdate: (id: string) => url + `/category/${id}/update`,
    categoryUpdateItem: (id: string, key: CategoryUpdateKey) => url + `/category/${id}/${key}/update`,
    categorySetItem: (id: string) => url + `/category/${id}/orgs/set`,
    categoryDel: (id: string) => url + `/category/${id}/del`,
    productUpdate: (id: string) => url + `/${id}/update`,
    productUpdateName: (id: string) => url + `/${id}/name/update`,
    productUpdateModel: (id: string) => url + `/${id}/model/update`,
    productUpdateDesc: (id: string) => url + `/${id}/desc/update`,
    productUpdateCbm: (id: string) => url + `/${id}/cbm/update`,
    productUpdateProcess: (id: string) => url + `/${id}/process/update`,
    productPrice: `${url}/purchase/prices`,
    unitTypes: `${url}/unit/types`,
    categroyList: `${urlCategory}/products`,
    priceList: (approveState: any) => `${urlCategory}/approveState/-1/list`,
    refuse: (id: string) => `${urlCategory}/${id}/refuse`,
    approve: (id: string) => `${urlCategory}/${id}/approve`,
    deprecated: (id: string) => `${url}/${id}/deprecated/update`,
    replacementId: (id: string) => `${url}/${id}/replacementId/update`,
    inventoryStandardList: url + '/inventoryStandard/list',
    setInventoryStandard: (id: string) => `${url}/inventoryStandard/${id}/set`,
    productReqList: (id: string) => `${url}/${id}/storageRequirement/list`,
    productReqAdd: (id: string) => `${url}/${id}/storageRequirement/add`,
    productReqDel: (id: string, itemId: string) => `${url}/${id}/storageRequirement/${itemId}/del`,
    productReqUpdate: (id: string, itemId: string) => `${url}/${id}/storageRequirement/${itemId}/update`,
}

export const categoryAdd = (category: CategoryAdd) => {
    if ((category.pId && !isString(category.pId)) || !isString(category.code) || !isString(category.name) || !isString(category.desc)) {
        return Promise.reject(10012)
    }
    return request<ProductCategory, CategoryAdd>(PRODUCT_APIS.categoryAdd, { params: { body: category } })
        .then(({ body: res }) => {
            if (isCategory(res)) {
                return Promise.resolve(cleanToCategory(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const categoryFetch = (id: string, controller?: AbortController) => {
    if (!isString(id) || isStringEmpty(id)) {
        return Promise.reject(10012)
    }
    return request<ProductCategory, string>(PRODUCT_APIS.categoryFetch(id), { controller })
        .then(({ body: category }) => {
            if (isCategory(category)) {
                return Promise.resolve(cleanToCategory(category))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const categoryList = (params?: CommonListParams) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<ProductCategory>, string>(PRODUCT_APIS.categories, { params })
        .then(({ body: categories }) => {
            if (isArray<ProductCategory>(categories)) {
                return Promise.resolve({
                    body: categories.filter(category => isCategory(category))?.map(category => cleanToCategory(category)),
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const productPrice = (body?: Array<string>) => {
    return new Promise<Array<ProductPrice>>((resolve, reject) => {
        return request<Array<ProductPrice>, Array<string>>(PRODUCT_APIS.productPrice, { params: { body } })
            .then(({ body }) => resolve(body))
            .catch(err => reject(err))
    });
}

export const categoriesFetch = (params?: CommonListParams, cateOrgPermission?: boolean) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<ProductCategory>, string>(
        PRODUCT_APIS.categoriesFetch,
        {
            params,
            requestConfig: { headers: cateOrgPermission ? undefined : { org_id: getStorage('curOrgId') } }
        }
    )
        .then(({ body: categories }) => {
            if (isArray<ProductCategory>(categories)) {
                return Promise.resolve({
                    body: categories.filter(category => isCategory(category))?.map(category => cleanToCategory(category)),
                })
            } else {
                return Promise.resolve({ body: [] })
            }
        })
        .catch(err => Promise.reject(err))
}

export const productAdd = (product: ProductAdd) => {
    if ((product.categoryId && !isString(product.categoryId)) ||
        !isString(product.code) || !isString(product.name) ||
        (product.desc && !isString(product.desc)) ||
        (product.model && !isString(product.model)) ||
        (product.uom && !isString(product.uom))) {
        return Promise.reject(10012)
    }
    return request<Product, ProductAdd>(PRODUCT_APIS.productAdd, { params: { body: product } })
        .then(({ body: res }) => {
            if (isProduct(res)) {
                return Promise.resolve(cleanToProduct(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const productSAdd = (product: ProductAdd, sApi: string) => {
    if ((product.categoryId && !isString(product.categoryId)) ||
        !isString(product.code) || !isString(product.name) ||
        (product.desc && !isString(product.desc)) ||
        (product.model && !isString(product.model)) ||
        (product.uom && !isString(product.uom))) {
        return Promise.reject(10012)
    }
    return request<Product, ProductAdd>(PRODUCT_APIS.productSAdd, { params: { body: product }, requestConfig: { baseURL: sApi } })
        .then(({ body: res }) => {
            if (isProduct(res)) {
                return Promise.resolve(cleanToProduct(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const categoryProductAdd = (id: string, product: ProductAdd) => {
    if ((!isString(id) || isStringEmpty(id) || (product.categoryId && !isString(product.categoryId))) || !isString(product.code) || !isString(product.name) || (product.desc && !isString(product.desc)) || (product.model && !isString(product.model))) {
        return Promise.reject(10012)
    }
    return request<Product, ProductAdd>(PRODUCT_APIS.categoryProductAdd(id), { params: { body: product } })
        .then(({ body: res }) => {
            if (isProduct(res)) {
                return Promise.resolve(cleanToProduct(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const productFetch = (id: string) => {
    if (!isString(id) || isStringEmpty(id)) {
        return Promise.reject(10012)
    }
    return request<Product, string>(PRODUCT_APIS.productFetch(id))
        .then(({ body: product }) => {
            if (isProduct(product)) {
                return Promise.resolve(cleanToProduct(product))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export interface ProductListBody {
    keyword?: string;
    preReady?: number;
    rootCategories?: string[];
}
export interface ProductListParams {
    categoryId?: string;
    params?: CommonListParams<string | undefined>;
    wmsApi?: string;
}
export interface ProductListObjParams {
    categoryId?: string;
    params?: CommonListParams<ProductListBody>;
    wmsApi?: string;
}

export const productList = (params?: ProductListObjParams) => {
    return request<CommonListParams<Product>, ProductListBody>(PRODUCT_APIS.productList(params?.categoryId),
        {
            params: params?.params,
            requestConfig: params?.wmsApi ? { baseURL: params.wmsApi } : undefined
        })
        .then(({ body: products, pagination }) => {
            if (isArray<Product>(products)) {
                return Promise.resolve({
                    body: products.filter(product => isProduct(product))?.map(product => cleanToProduct(product)),
                    pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const categroyList = (params?: ProductListParams) => {
    if (params && params.params?.body && !isString(params?.params.body)) {
        return Promise.reject(10012)
    }
    return request<CommonListParams<Product>, string>(PRODUCT_APIS.categroyList, { params: params?.params })
        .then(({ body: products, pagination }) => {
            if (isArray<Product>(products)) {
                return Promise.resolve({
                    body: products.filter(product => isProduct(product))?.map(product => cleanToProduct(product)),
                    pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const priceList = (state: any, params?: ProductListParams) => {
    if (params && params.params?.body && !isString(params?.params.body)) {
        return Promise.reject(10012)
    }
    return request<CommonListParams<Product>, string>(PRODUCT_APIS.priceList(state.categoryId), { params: state?.params })
        .then(({ body, pagination }) => {
            if (isArray<Product>(body)) {
                return Promise.resolve({
                    body: body.map(product => cleanToProduct(product)),
                    pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const refuse = (id: any) => {
    if (!id) {
        return Promise.reject(10012)
    }
    return request<CommonListParams<Product>, string>(PRODUCT_APIS.refuse(id))
        .then((res) => {
            if (res) {
                return Promise.resolve(res)
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const approve = (id: any) => {
    if (!id) {
        return Promise.reject(10012)
    }
    return request<CommonListParams<Product>, string>(PRODUCT_APIS.approve(id))
        .then((res) => {
            if (res) {
                return Promise.resolve(res)
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const productListByRootCategory = (id: string, params?: CommonListParams) => {
    if (!isString(id) || isStringEmpty(id) || (params && params.body && !isString(params.body))) {
        return Promise.reject(10012)
    }
    return request<CommonListParams, string>(PRODUCT_APIS.productListByRootCategory(id), { params })
        .then((res) => {
            const products = res.body;
            if (isArray<Product>(products)) {
                return Promise.resolve({
                    body: products.filter(product => isProduct(product))?.map(product => cleanToProduct(product)),
                    pagination: res.pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}



export const productFetchs = (params?: CommonListParams<ProductListBody>) => {

    return request<Array<Product>, ProductListBody>(PRODUCT_APIS.productFetchs, { params })
        .then(res => Promise.resolve(res))
        .catch(err => Promise.reject(err))
}

export const categoryProductFetch = (id: string, params?: CommonListParams) => {
    if (!isString(id) || isStringEmpty(id) || (params && params.body && !isString(params.body))) {
        return Promise.reject(10012)
    }
    return request<Array<Product>, string>(PRODUCT_APIS.categoryProductFetch(id), { params })
        .then(({ body: products }) => {
            if (isArray<Product>(products)) {
                return Promise.resolve(products.filter(product => isProduct(product))?.map(product => cleanToProduct(product)))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const categoryUpdate = (id: string, info: CategoryUpdate) => {
    if (!isString(id) || (isStringEmpty(id) &&
        (info.pId && !isString(info.pId)) &&
        !isString(info.code) && !isString(info.name) && !isString(info.desc))) {
        return Promise.reject(10012)
    }
    return request<void, CategoryUpdate>(PRODUCT_APIS.categoryUpdate(id), { params: { body: info } })
}

export const categoryUpdateItem = (id: string, key: CategoryUpdateKey, value: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (value && !isString(value))) {
        return Promise.reject(10012)
    }
    return request<void, string>(PRODUCT_APIS.categoryUpdateItem(id, key), { params: { body: value } })
}

export const categorySetItem = (id: string, values: string[]) => {
    if ((!isString(id) || isStringEmpty(id))) {
        return Promise.reject(10012)
    }
    return request<void, string[]>(PRODUCT_APIS.categorySetItem(id), { params: { body: values } })
}

export const categoryDel = (id: string) => {
    if ((!isString(id) || isStringEmpty(id))) {
        return Promise.reject(10012)
    }
    return request<void>(PRODUCT_APIS.categoryDel(id))
}

export const productUpdate = (id: string, info: ProductUpdate) => {
    if (!isString(id) || (isStringEmpty(id) &&
        (info.categoryId && !isString(info.categoryId)) &&
        !isString(info.code) && !isString(info.name) &&
        (info.desc && !isString(info.desc)) &&
        (info.model && !isString(info.model)))) {
        return Promise.reject(10012)
    }
    return request<void, ProductUpdate>(PRODUCT_APIS.productUpdate(id), { params: { body: info } })
}

export const productUpdateName = (id: string, name: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (name && !isString(name))) {
        return Promise.reject(10012)
    }
    return request<void>(PRODUCT_APIS.productUpdateName(id))
}

export const productUpdateModel = (id: string, model: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (model && !isString(model))) {
        return Promise.reject(10012)
    }
    return request<void>(PRODUCT_APIS.productUpdateModel(id))
}

export const productUpdateDesc = (id: string, desc: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (desc && !isString(desc))) {
        return Promise.reject(10012)
    }
    return request<void>(PRODUCT_APIS.productUpdateDesc(id))
}

export const productUpdateCbm = (id: string, cbm: string) => {
    if ((!isString(id) || isStringEmpty(id))) {
        return Promise.reject(10012)
    }
    return request<void, string>(PRODUCT_APIS.productUpdateCbm(id), { params: { body: cbm } })
}

export const productUpdateProcess = (id: string, process: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (process && !isString(process))) {
        return Promise.reject(10012)
    }
    return request<void, string>(PRODUCT_APIS.productUpdateProcess(id), { params: { body: process } })
}

export interface ProductUnitType {
    key: string;
    desc: string
}

export const productUnitTypes = () => {
    return request<ProductUnitType[], undefined>(PRODUCT_APIS.unitTypes)
        .then(({ body }) => {
            if (body && body.length > 0)
                return Promise.resolve(body)
            else return Promise.resolve([])
        })
        .catch(err => Promise.reject(10013))
}

export const deprecated = (id: string, data: ProductDeprecatedEnum | null) => {
    return new Promise<Product>((resolve, reject) => {
        request<Product, ProductDeprecatedEnum | null>(PRODUCT_APIS.deprecated(id), { params: { body: data } })
            .then(res => resolve(res.body))
            .catch(err => reject(err))
    })
}

export const replacementId = (id: string, data: string) => {
    return new Promise<Product>((resolve, reject) => {
        request<Product, string>(PRODUCT_APIS.replacementId(id), { params: { body: data } })
            .then(res => resolve(res.body))
            .catch(err => reject(err))
    })
};


export const inventoryStandardList = (params?: CommonListParams) => {
    return new Promise<CommonListParams<InventoryStandard[]>>((resolve, reject) => {
        request<InventoryStandard[], string>(PRODUCT_APIS.inventoryStandardList, { params })
            .then(res => resolve(res))
            .catch(err => reject(err))
    })
};

export const setInventoryStandard = (data: AddInventoryStandard) => {
    let { productId, ...info } = { ...data }
    return new Promise<InventoryStandard>((resolve, reject) => {
        request<InventoryStandard, Omit<AddInventoryStandard, "productId">>(PRODUCT_APIS.setInventoryStandard(productId), { params: { body: info } })
            .then(({ body }) => resolve(body))
            .catch(err => reject(err))
    })
};

export const productReqList = (id: string, params?: CommonListParams) => {
    return new Promise<CommonListParams<ProductStoreRequire[]>>((resolve, reject) => {
        request<ProductStoreRequire[], string>(PRODUCT_APIS.productReqList(id), { params })
            .then(res => resolve(res))
            .catch(err => reject(err))
    })
};

export const productReqAdd = (id: string, body: ProductStoreRequireAdd) => {
    return new Promise<ProductStoreRequire>((resolve, reject) => {
        request<ProductStoreRequire, ProductStoreRequireAdd>(PRODUCT_APIS.productReqAdd(id), { params: { body } })
            .then(({ body }) => resolve(body))
            .catch(err => reject(err))
    })
};

export const productReqUpdate = (id: string, itemId: string, body: ProductStoreRequireAdd) => {
    return new Promise<ProductStoreRequire>((resolve, reject) => {
        request<ProductStoreRequire, ProductStoreRequireAdd>(PRODUCT_APIS.productReqUpdate(id, itemId), { params: { body } })
            .then(({ body }) => resolve(body))
            .catch(err => reject(err))
    })
};

export const productReqDel = (id: string, itemId: string) => {
    return new Promise<void>((resolve, reject) => {
        request<void, undefined>(PRODUCT_APIS.productReqDel(id, itemId))
            .then(() => resolve())
            .catch(err => reject(err))
    })
};
